import { cva } from 'class-variance-authority';

import { cn } from '@heidi/utils';

import {
  controlFieldStateText,
  controlFocusWithin,
  controlHeight,
  controlHeightCompact,
  controlInvalidWithin,
  controlSurface,
  controlSurfaceDisabled,
  controlSurfaceHoverWithin,
  controlTransition,
  controlValueText,
  controlValueTextCompact,
} from '../styles/control';

/**
 * Experimental Input styles, behind `release-experimental-design-system-forms`.
 * Adopts the shared `control` tier; stable keeps its raw `text-sm` / `h-[38px]`.
 */

export const wrapperVariantsExperimental = cva(
  cn(
    'flex items-center rounded-md',
    controlSurface,
    controlTransition,
    controlSurfaceHoverWithin,
    controlFocusWithin,
    // After `controlFocusWithin`, so tailwind-merge resolves the ring/border
    // pair down to the destructive one on an invalid field. The `:focus-visible`
    // restatement inside it handles the focused-and-invalid overlap.
    controlInvalidWithin
  ),
  {
    variants: {
      density: {
        // On the bordered element, matching Select/Combobox: border-box puts the
        // borders inside the 40px.
        comfortable: cn(controlHeight, 'gap-2 rounded-lg px-3'),
        compact: cn(controlHeightCompact, 'gap-x-1 px-2.5'),
      },
    },
    defaultVariants: {
      density: 'comfortable',
    },
  }
);

// Unprefixed — the wrapper is a `<div>`, so Input applies it conditionally.
export const wrapperDisabledClassesExperimental = cn(
  controlSurfaceDisabled,
  'text-control-text-value-disabled [&_button_svg]:text-current [&_svg]:text-control-icon-disabled'
);

export const inputVariantsExperimental = cva(
  cn(
    'flex w-full truncate border-0 bg-transparent file:bg-transparent file:font-medium focus-visible:outline-none disabled:cursor-not-allowed',
    controlTransition,
    // In the base, not a density variant: both densities tint the same.
    controlFieldStateText
  ),
  {
    variants: {
      density: {
        // Height comes from the wrapper. Both densities take their type from the
        // tier, so only the step moves — weight and colour can't drift apart.
        comfortable: cn('h-full', controlValueText, 'file:text-sm'),
        compact: cn('h-full', controlValueTextCompact, 'file:text-xs'),
      },
    },
    defaultVariants: {
      density: 'comfortable',
    },
  }
);
